import React, { useState, useEffect } from 'react';
import { SecondaryBase } from '../Commons/SecondaryBase';
import { useAuth } from '../../Providers/AuthProvider';
import { Confirm } from './../../Commons/Modals/Confirm';
import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage';

export default function PlayersManager({ getOutOfTheDynamic }) {
  const { authenticatedFetch } = useAuth();
  const { showMessage } = useFloatMessage();

  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedTeamFilter, setSelectedTeamFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);

  // Form states
  const [playerName, setPlayerName] = useState('');
  const [playerDorsal, setPlayerDorsal] = useState('');
  const [playerTeamId, setPlayerTeamId] = useState('');
  const [playerPosition, setPlayerPosition] = useState('Medio');
  const [photoUrl, setPhotoUrl] = useState('');
  const [playerRole, setPlayerRole] = useState('Jugador');
  const [isActive, setIsActive] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Confirm modal states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:50586/api/';

  const openConfirmationModal = (messageToDisplay, actionFunctionToExecute) => {
    setConfirmModalData({
      message: messageToDisplay,
      actionToYesButton: actionFunctionToExecute,
      textYesButton: 'Sí, confirmar',
      textNoButton: 'Cancelar'
    });
    setShowConfirmModal(true);
  };

  const fetchTeams = async () => {
    try {
      const res = await authenticatedFetch(`${API_URL}Teams`);
      if (res.ok) {
        const result = await res.json();
        if (result.success) setTeams(result.data);
      }
    } catch (err) {
      console.error('Error loading teams', err);
    }
  };

  const fetchPlayers = async () => {
    setLoading(true);
    try {
      const url = selectedTeamFilter ? `${API_URL}Players?teamId=${selectedTeamFilter}` : `${API_URL}Players`;
      const res = await authenticatedFetch(url);
      if (res.ok) {
        const result = await res.json();
        if (result.success) {
          setPlayers(result.data);
        } else {
          showMessage(result.message || 'Error al cargar jugadores', MESSAGE_TYPE.BAD);
        }
      }
    } catch (err) {
      showMessage('No se pudo conectar con el servidor.', MESSAGE_TYPE.BAD);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeams();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchPlayers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeamFilter]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      showMessage('El archivo excede el tamaño máximo permitido de 2MB.', MESSAGE_TYPE.WARNING);
      return;
    }

    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'players');

    try {
      const res = await fetch(`${API_URL}Upload/image`, {
        method: 'POST',
        body: formData
      });
      const result = await res.json();
      if (result.success) {
        setPhotoUrl(result.url);
        showMessage('Foto subida con éxito.', MESSAGE_TYPE.SUCCESS);
      } else {
        showMessage(result.message || 'Error al subir la foto.', MESSAGE_TYPE.BAD);
      }
    } catch (err) {
      showMessage('Error de red al subir la imagen.', MESSAGE_TYPE.BAD);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!playerName.trim()) {
      showMessage('El nombre del integrante es obligatorio.', MESSAGE_TYPE.WARNING);
      return;
    }

    if (!playerTeamId) {
      showMessage('Debes asignar un equipo al integrante.', MESSAGE_TYPE.WARNING);
      return;
    }

    const payload = {
      name: playerName,
      dorsal: parseInt(playerDorsal) || 0,
      teamId: parseInt(playerTeamId),
      position: playerPosition,
      photoUrl: photoUrl,
      role: playerRole,
      isActive: isActive
    };

    try {
      const url = editingPlayer ? `${API_URL}Players/${editingPlayer.id}` : `${API_URL}Players`;
      const method = editingPlayer ? 'PUT' : 'POST';

      const res = await authenticatedFetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await res.json();
      if (result.success) {
        showMessage(editingPlayer ? 'Integrante actualizado con éxito.' : 'Integrante registrado con éxito.', MESSAGE_TYPE.SUCCESS);
        resetForm();
        fetchPlayers();
      } else {
        showMessage(result.message || 'Error al guardar integrante.', MESSAGE_TYPE.BAD);
      }
    } catch (err) {
      showMessage('Error de red al guardar.', MESSAGE_TYPE.BAD);
    }
  };

  const startEdit = (player) => {
    setEditingPlayer(player);
    setPlayerName(player.name);
    setPlayerDorsal(player.dorsal.toString());
    setPlayerTeamId(player.teamId.toString());
    setPlayerPosition(player.position || 'Medio');
    setPhotoUrl(player.photoUrl || '');
    setPlayerRole(player.role || 'Jugador');
    setIsActive(player.isActive);
  };

  const handleDelete = (player) => {
    openConfirmationModal(
      `¿Estás seguro de que deseas desactivar al integrante "${player.name}"?`,
      async () => {
        try {
          const res = await authenticatedFetch(`${API_URL}Players/${player.id}`, {
            method: 'DELETE'
          });
          const result = await res.json();
          if (result.success) {
            showMessage('Integrante desactivado con éxito.', MESSAGE_TYPE.SUCCESS);
            fetchPlayers();
          } else {
            showMessage(result.message || 'Error al desactivar al integrante.', MESSAGE_TYPE.BAD);
          }
        } catch (err) {
          showMessage('Error al conectar con la base de datos.', MESSAGE_TYPE.BAD);
        }
      }
    );
  };

  const resetForm = () => {
    setEditingPlayer(null);
    setPlayerName('');
    setPlayerDorsal('');
    setPlayerTeamId('');
    setPlayerPosition('Medio');
    setPhotoUrl('');
    setPlayerRole('Jugador');
    setIsActive(true);
  };

  return (
    <SecondaryBase actionToExecute={getOutOfTheDynamic}>
      <div className="container-fluid pb-5">
        <div className="text-center mb-4 text-white">
          <h3 className="fw-bold"><i className="bi bi-people-fill me-2 text-info"></i>Catálogo Maestro de Plantillas (Roster)</h3>
          <p className="text-muted small">Crea y administra la nómina de jugadores y cuerpo técnico de todos los equipos del torneo.</p>
        </div>

        <div className="row g-4">
          {/* Formulario */}
          <div className="col-lg-4">
            <div className="card shadow-sm border-0 rounded-4 text-white" style={{ background: '#111827' }}>
              <div className="card-header bg-dark text-white fw-bold d-flex justify-content-between align-items-center" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <span>{editingPlayer ? 'Editar Integrante' : 'Nuevo Integrante'}</span>
                {editingPlayer && (
                  <button className="btn btn-xs btn-outline-warning" onClick={resetForm}>
                    Nuevo
                  </button>
                )}
              </div>
              <div className="card-body">
                <form onSubmit={handleSave}>
                  <div className="mb-3">
                    <label className="form-label small fw-bold">Nombre Completo</label>
                    <input
                      type="text"
                      className="form-control bg-dark border-secondary text-white"
                      value={playerName}
                      onChange={(e) => setPlayerName(e.target.value)}
                      placeholder="Ej. Cristiano Ronaldo"
                    />
                  </div>

                  <div className="row g-2 mb-3">
                    <div className="col-6">
                      <label className="form-label small fw-bold">Dorsal / Camiseta</label>
                      <input
                        type="number"
                        className="form-control bg-dark border-secondary text-white"
                        value={playerDorsal}
                        onChange={(e) => setPlayerDorsal(e.target.value)}
                        placeholder="Ej. 10"
                        min="0"
                        max="999"
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label small fw-bold">Posición de Juego</label>
                      <select
                        className="form-select bg-dark border-secondary text-white"
                        value={playerPosition}
                        onChange={(e) => setPlayerPosition(e.target.value)}
                      >
                        <option value="Portero">Portero</option>
                        <option value="Defensa">Defensa</option>
                        <option value="Medio">Medio</option>
                        <option value="Delantero">Delantero</option>
                        <option value="Suplente">Suplente</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label small fw-bold">Equipo</label>
                    <select
                      className="form-select bg-dark border-secondary text-white"
                      value={playerTeamId}
                      onChange={(e) => setPlayerTeamId(e.target.value)}
                    >
                      <option value="">-- Asigna un equipo --</option>
                      {teams.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label small fw-bold">Rol en el Equipo</label>
                    <select
                      className="form-select bg-dark border-secondary text-white"
                      value={playerRole}
                      onChange={(e) => setPlayerRole(e.target.value)}
                    >
                      <option value="Jugador">Jugador</option>
                      <option value="Director Técnico">Director Técnico</option>
                      <option value="Auxiliar Técnico">Auxiliar Técnico</option>
                      <option value="Médico">Médico / Kinesiólogo</option>
                      <option value="Utilero">Utilero</option>
                      <option value="Staff">Otro Staff</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label small fw-bold">Fotografía del Integrante</label>
                    <input
                      type="file"
                      className="form-control bg-dark border-secondary text-white mb-2"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                    />
                    {uploadingPhoto && <div className="spinner-border spinner-border-sm text-info mb-2" role="status" />}
                    
                    {photoUrl && (
                      <div className="d-flex align-items-center gap-2 bg-black bg-opacity-50 p-2 rounded border border-secondary mt-2">
                        <img 
                          src={photoUrl.startsWith('http') ? photoUrl : `${API_URL.replace('/api/', '')}${photoUrl}`} 
                          alt="Photo Preview" 
                          style={{ width: '45px', height: '45px', objectFit: 'cover', borderRadius: '50%' }}
                          onError={(e) => e.target.style.display = 'none'}
                        />
                        <span className="small text-muted text-truncate flex-fill">{photoUrl}</span>
                        <button type="button" className="btn btn-sm btn-danger py-0 px-1" onClick={() => setPhotoUrl('')}>x</button>
                      </div>
                    )}
                  </div>

                  <div className="mb-3 form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="playerActiveSwitch"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                    />
                    <label className="form-check-label small fw-bold text-white" htmlFor="playerActiveSwitch">
                      Integrante Activo
                    </label>
                  </div>

                  <button type="submit" className="btn btn-info w-100 fw-bold text-dark mt-2 shadow-sm">
                    <i className="bi bi-cloud-arrow-up-fill me-1"></i> Guardar Integrante
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Listado */}
          <div className="col-lg-8">
            <div className="card shadow-sm border-0 rounded-4 text-white animate-fade-in" style={{ background: '#111827' }}>
              <div className="card-header bg-dark fw-bold text-white d-flex justify-content-between align-items-center" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="d-flex align-items-center gap-3 w-100 justify-content-between">
                  <span>Listado de Plantilla (Roster)</span>
                  <div style={{ minWidth: '220px' }}>
                    <select
                      className="form-select form-select-sm bg-dark border-secondary text-white"
                      value={selectedTeamFilter}
                      onChange={(e) => setSelectedTeamFilter(e.target.value)}
                    >
                      <option value="">-- Todos los Equipos --</option>
                      {teams.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center p-5">
                    <div className="spinner-border text-info" role="status" />
                  </div>
                ) : players.length === 0 ? (
                  <div className="text-center p-5 text-muted">No hay integrantes registrados para esta selección.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-dark table-hover align-middle mb-0" style={{ background: 'transparent' }}>
                      <thead>
                        <tr>
                          <th>Foto</th>
                          <th>Dorsal</th>
                          <th>Nombre</th>
                          <th>Rol</th>
                          <th>Posición</th>
                          <th>Equipo</th>
                          <th>Estatus</th>
                          <th className="text-end">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {players.map((p) => {
                          const playerTeamName = teams.find(t => t.id === p.teamId)?.name || `ID: ${p.teamId}`;
                          return (
                            <tr key={p.id}>
                              <td>
                                {p.photoUrl ? (
                                  <img
                                    src={p.photoUrl.startsWith('http') ? p.photoUrl : `${API_URL.replace('/api/', '')}${p.photoUrl}`}
                                    alt="Foto"
                                    style={{ width: '35px', height: '35px', objectFit: 'cover', borderRadius: '50%' }}
                                    onError={(e) => { e.target.src = '/img/games/lineup/default_avatar.png'; }}
                                  />
                                ) : (
                                  <i className="bi bi-person-circle text-muted fs-4"></i>
                                )}
                              </td>
                              <td>
                                <span className="badge bg-secondary font-monospace">#{p.dorsal}</span>
                              </td>
                              <td>
                                <span className="fw-bold">{p.name}</span>
                              </td>
                              <td>
                                <span className="badge bg-dark border border-secondary text-info text-xs">{p.role || 'Jugador'}</span>
                              </td>
                              <td>
                                <span className="small opacity-75">{p.position || 'General'}</span>
                              </td>
                              <td>
                                <span className="small text-info">{playerTeamName}</span>
                              </td>
                              <td>
                                <span className={`badge ${p.isActive ? 'bg-success' : 'bg-danger'}`}>
                                  {p.isActive ? 'Activo' : 'Inactivo'}
                                </span>
                              </td>
                              <td className="text-end">
                                <div className="d-flex gap-1 justify-content-end">
                                  <button className="btn btn-sm btn-outline-info" onClick={() => startEdit(p)} title="Editar">
                                    <i className="bi bi-pencil-fill"></i>
                                  </button>
                                  {p.isActive && (
                                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(p)} title="Desactivar">
                                      <i className="bi bi-trash-fill"></i>
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showConfirmModal && (
        <Confirm
          show={showConfirmModal}
          data={confirmModalData}
          onHide={() => setShowConfirmModal(false)}
        />
      )}
    </SecondaryBase>
  );
}
